import _ from 'lodash';
import { t } from '@lingui/macro';

import Panel from '../../page-framework/panel/PanelDecorator';
import {
    PANEL_CATEGORIES,
    PANEL_PRIORITIES,
    STATUS_LIST_PANEL_PREVIEW_IMAGE,
} from '../../page-framework/panel/PanelRegistry';
import { panelPropTypes, panelPropDefaults } from '../../page-framework/panel/PanelPropTypes';
import { COLUMN_TYPES } from '../../data-table/DataTableColumns';
import { useQueryLoader } from '../../data-table/Loaders';
import { filterId as serverFilterId } from '../../filters/server-filter/ServerFilter';
import { filterId as vmFilterId } from '../../filters/virtual-machine-filter/VirtualMachineFilter';
import { filterId as hypervisorFilterId } from '../../filters/hypervisor-filter/HypervisorFilter';
import ReportingDataTable from '../../data-table/ReportingDataTable';
import { metricDefFormatter } from '../../../utilities';
import { listTooltip, percentConfig, percentSparkLinkFn } from './funcs/IpmList';
import {
    businessServiceFilterId,
    serverTypeFilterId,
    siteFilterId,
    statusFilterId,
    timeRangeFilterId,
} from '../../filters';

const title = t`Servers`;

const dataSource = {
    url: '/query/table',
    params: {
        type: 'server',
        entityFields: ['ipAddress', 'name', 'subType'],
        host: true,
        metrics: ['status', 'avgCpu', 'avgMem', 'avgDisk'],
        trends: ['avgCpu', 'avgMem', 'status'],
        renderAsEntity: true,
    },
};

const subTypes = {
    server: t`Server`,
    basicServer: t`Basic Server`,
    vcenter: t`vCenter`,
    hypervisor: t`Hypervisor`,
    host: t`Hypervisor`,
    virtualmachine: t`Virtual Machine`,
};

const linker = (row) => {
    const selector = _.get(row, 'server.subType');
    let target;
    let filterId;
    switch (selector) {
        case 'virtualmachine':
            target = 'vmware-vm-detail';
            filterId = vmFilterId;
            break;
        case 'hypervisor':
            target = 'vmware-host-detail';
            filterId = hypervisorFilterId;
            break;
        default:
            target = 'server-detail';
            filterId = serverFilterId;
    }
    return {
        target,
        mappedFilterParams: {
            [filterId]: 'server',
        },
    };
};

const CPU = t`CPU`;
const MEMORY = t`Memory`;

const columns = [
    {
        type: COLUMN_TYPES.DRILLINLINK,
        title: t`Server`,
        sortable: true,
        mapping: 'server.name',
        getTooltip: listTooltip('server.name'),
        width: 150,
        link: linker,
    },
    {
        type: COLUMN_TYPES.DRILLINLINK,
        title: t`IP Address`,
        sortable: true,
        mapping: 'server.ipAddress',
        getTooltip: listTooltip('server.ipAddress'),
        width: 120,
        link: linker,
    },
    {
        type: COLUMN_TYPES.TEXT,
        title: t`Type`,
        sortable: true,
        sortProperty: 'server.subType',
        mapper: (row) => subTypes[row?.server?.subType || 'server'],
        width: 120,
    },
    {
        type: COLUMN_TYPES.NUMERIC,
        title: '',
        assocId: CPU, // associated with CPU column
        csvTitle: CPU,
        mapping: 'avgCpu',
        width: 60,
        formatter: metricDefFormatter(percentConfig),
    },
    {
        type: COLUMN_TYPES.SPARKLINE,
        title: CPU,
        sortProperty: 'avgCpu',
        sortable: true,
        width: 100,
        mapper: (rowData) => percentSparkLinkFn('avgCpu', 'CPU Usage')(rowData),
    },
    {
        type: COLUMN_TYPES.NUMERIC,
        title: '',
        assocId: MEMORY, // associated with Memory column
        csvTitle: MEMORY,
        mapping: 'avgMem',
        width: 60,
        formatter: metricDefFormatter(percentConfig),
    },
    {
        type: COLUMN_TYPES.SPARKLINE,
        title: MEMORY,
        sortProperty: 'avgMem',
        sortable: true,
        width: 100,
        mapper: (rowData) => percentSparkLinkFn('avgMem', 'Mem Usage')(rowData),
    },
    {
        type: COLUMN_TYPES.NUMERIC,
        title: t`Disk`,
        mapping: 'avgDisk',
        sortable: true,
        width: 80,
        formatter: metricDefFormatter(percentConfig),
    },
    {
        type: COLUMN_TYPES.STATUSTREND,
        title: t`Status Trend`,
        sortable: true,
        sortProperty: 'status',
        mapping: 'trends.status.data',
        minWidth: 240,
    },
];

const sorters = [{ property: 'status', direction: 'desc' }];

function ServerListPanel(props) {
    const loader = useQueryLoader(dataSource);
    return (
        <ReportingDataTable
            panelProps={props}
            loader={loader}
            columns={columns}
            sorters={sorters}
            deletedMapping="server.deleted"
        />
    );
}

ServerListPanel.propTypes = panelPropTypes;

ServerListPanel.defaultProps = {
    ...panelPropDefaults,
    title,
    autoHeight: true,
    minContentHeight: 130,
};

export default Panel({
    typeAlias: 'ServerList',
    catalogEntry: {
        category: PANEL_CATEGORIES.SERVER,
        priority: PANEL_PRIORITIES.LIST,
        title,
        description: t`Lists of servers, showing status trend, CPU utilization, memory utilization, and disk utilization`,
        previewImage: STATUS_LIST_PANEL_PREVIEW_IMAGE,
        ipm: true,
    },
    filters: {
        required: [timeRangeFilterId],
        optional: [businessServiceFilterId, hypervisorFilterId, serverTypeFilterId, siteFilterId, statusFilterId],
    },
})(ServerListPanel);
